import "./guide.css";
import React from "react";
import {
  Rule,
  Schedule,
  absorbedBy,
  triggerSentence,
  whiteBadgeFill,
} from "./api";
import { Chip, Chips, byRank, codes } from "./GuideChips";
import {
  Strip,
  StripPlan,
  Truncation,
  cycleRules,
  occupiedStrip,
  vacantStrip,
} from "./GuideCycle";
// The column answers what still has to happen anyway if this clean is recorded,
// so it names every OTHER kind of work the rule leaves standing. Rank plays no
// part: telling a rule it does not settle work of its own kind is the only noise.
const CATEGORIES: Record<string, string> = {
  departure: "Departure work",
  turnaround: "Departure work",
  arrival: "The next guest's arrival check",
  occupiedInterval: "Routine cleaning",
  vacantInterval: "Routine cleaning",
};
const CATEGORY_ORDER = [
  "Departure work",
  "The next guest's arrival check",
  "Routine cleaning",
];
function categoryOf(rule: Rule): string {
  return CATEGORIES[rule.trigger] ?? "Routine cleaning";
}
function joinNaturally(parts: string[]): string {
  return parts.length <= 1
    ? parts.join("")
    : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}
function notSettled(active: Rule[], rule: Rule): string {
  const covers = rule.coverageRuleIds ?? [];
  const others = active.filter((other) => other.id !== rule.id);
  if (others.every((other) => covers.includes(other.id)))
    return "Nothing. It settles everything.";
  if (
    rule.trigger === "arrival" &&
    others.every((other) => !covers.includes(other.id))
  )
    return "Any cleaning. It is a presentation check.";
  const own = categoryOf(rule);
  const standing = others.filter((other) => !covers.includes(other.id));
  const named = CATEGORY_ORDER.filter(
    (category) =>
      category !== own &&
      standing.some((other) => categoryOf(other) === category),
  );
  // When the only work left standing is its own kind, that is the honest answer.
  return joinNaturally(named.length > 0 ? named : [own]);
}
// The concept led with the cleans that settle other work, then closed with the
// ones that settle only themselves; each half stays in rank order.
function absorptionRows(active: Rule[]) {
  const rows = active.map((rule) => ({
    rule,
    settles: active.filter(
      (other) =>
        other.id !== rule.id && (rule.coverageRuleIds ?? []).includes(other.id),
    ),
  }));
  return [
    ...rows.filter((row) => row.settles.length > 0),
    ...rows.filter((row) => row.settles.length === 0),
  ];
}
function dayRows(rules: Rule[]) {
  const of = (trigger: string) => rules.filter((r) => r.trigger === trigger);
  const arrival = of("arrival"),
    departure = of("departure"),
    turnaround = of("turnaround"),
    occupied = of("occupiedInterval"),
    vacant = of("vacantInterval");
  return [
    {
      title: "Check-in",
      trigger: "arrival",
      appears: (
        <>
          <Chips rules={arrival} /> presentation check.
        </>
      ),
      watch: (
        <>
          If the room was never prepared, a <Chips rules={vacant} /> is raised
          alongside it and {codes(arrival)} cannot be marked done until the{" "}
          {codes(vacant)} is recorded.
        </>
      ),
    },
    {
      title: "Check-out",
      trigger: "departure",
      appears: (
        <>
          <Chips rules={departure} /> departure clean.
        </>
      ),
      watch: (
        <>
          Any stayover cleans still outstanding for that guest are absorbed into
          the {codes(departure)}. Two bookings ending the same day still raise
          one {codes(departure)}.
        </>
      ),
    },
    {
      title: "Same day both",
      trigger: "turnaround",
      appears: (
        <>
          <Chips rules={turnaround} /> only.
        </>
      ),
      watch: (
        <>
          No separate {codes(departure)} and no {codes(arrival)}. The arriving
          guest must be staying the night, otherwise the day is an ordinary
          check-out.
        </>
      ),
    },
    {
      title: "Guest in house",
      trigger: "occupied",
      appears: (
        <>
          <Chips rules={byRank(occupied)} join="or" /> on their cycle days, plus
          anything carried over.
        </>
      ),
      watch: <>Nothing is raised on the day the guest checks out.</>,
    },
    {
      title: "Room empty",
      trigger: "vacant",
      appears: (
        <>
          <Chips rules={byRank(vacant)} /> on its cycle day, plus any{" "}
          {codes(departure)} not yet done.
        </>
      ),
      watch: (
        <>
          A room the system has never seen occupied gets one {codes(vacant)}{" "}
          dated today rather than an invented backlog.
        </>
      ),
    },
  ];
}
export function Guide({ data }: { data: Schedule }) {
  const active = byRank((data.rules ?? []).filter((rule) => !rule.retired));
  if (active.length === 0)
    return (
      <div className="hk-guide">
        <section className="pfres-section">
          <div className="pfres-section__header">
            <h2 className="pfres-section__title">Guide</h2>
            <p className="pfres-section__lede">
              No cleaning rules have been set up yet. Once a manager adds them,
              this page explains what each one is and when it is raised.
            </p>
          </div>
        </section>
      </div>
    );
  const deepest = active[active.length - 1];
  const occupied = occupiedStrip(active);
  const vacant = vacantStrip(active);
  // Only a collision the deeper clean actually covers is one the sheet merges.
  const absorbing = occupied.cells.find((cell) =>
    cell.due.some((mark) => mark.absorber),
  );
  const absorbed = absorbing?.due.filter((mark) => mark.absorber) ?? [];
  const absorber = absorbed[0]?.absorber;
  return (
    <div className="hk-guide">
      <section className="pfres-section">
        <div className="pfres-section__header">
          <h2 className="pfres-section__title">Cleaning types</h2>
          <p className="pfres-section__lede">
            Every clean we raise, lightest first. Each one has the code that
            appears on the paper sheet, the minutes it is allowed, when it is
            raised, the deeper cleans that can absorb it, and its full scope as
            printed on the back of the paper sheet.
          </p>
        </div>
        <div className="pfres-section__body">
          {/* One marker column for the whole list, set on the list and never
              per row, so every title starts on the same edge however wide the
              code is. */}
          <ul className="pfres-marker-list">
            {active.map((rule) => {
              const absorbers = absorbedBy(active, rule);
              return (
                <li
                  className="pfres-marker-list__item"
                  key={rule.id}
                  style={
                    {
                      "--pfres-rule-fill": whiteBadgeFill(rule.color),
                    } as React.CSSProperties
                  }
                >
                  <div className="pfres-marker-row">
                    <div className="pfres-marker-row__marker">
                      <Chip rule={rule} />
                    </div>
                    <div className="pfres-marker-row__body">
                      <h3 className="pfres-marker-row__title">{rule.label}</h3>
                      <div className="hk-guide-when">
                        {triggerSentence(rule.trigger, rule.intervalNights)}
                        {rule.trigger === "turnaround" && rule.vacantArrivalNights != null &&
                          ` Also planned on arrival after ${rule.vacantArrivalNights}+ vacant nights, even without a same-day checkout.`}
                      </div>
                    </div>
                    <div className="pfres-marker-row__aside">
                      {rule.minutes} min
                    </div>
                    <div className="pfres-marker-row__sub">
                      <div className="hk-guide-covers">
                        <b>Absorbed by:</b>{" "}
                        {absorbers.length > 0 ? (
                          <Chips rules={byRank(absorbers)} />
                        ) : rule.id === deepest.id ? (
                          "nothing. This is the deepest clean."
                        ) : (
                          "nothing. Only recording it settles it."
                        )}
                      </div>
                      {(rule.description ?? "").trim() !== "" && (
                        <p className="pfres-marker-row__desc">
                          {rule.description}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
      <section className="pfres-section">
        <div className="pfres-section__header">
          <h2 className="pfres-section__title">The cleaning cycle</h2>
          <p className="pfres-section__lede">
            An occupied room counts from the guest&apos;s arrival night. An
            empty room counts from the day the last guest left. The strips
            below assume every due service is completed on time; recording a
            real completion moves each explicitly covered interval&apos;s next
            due to that date plus its own interval.
          </p>
        </div>
        <div className="pfres-section__body">
          <h3 className="pfres-section__subhead">
            While a guest is in the room
          </h3>
          <div className="hk-guide-unit">
            <Strip plan={occupied} />
            <p className="hk-guide-legend">
              The arrival night is night one. Each mark assumes the earlier
              marks were completed when due: a recorded completion resets every
              interval it covers, so a late or early clean moves the next due
              rather than the strip&apos;s fixed dates.
              {absorbing &&
                absorber &&
                ` On ${absorbing.spoken} a ${codes(
                  absorbed.map((mark) => mark.rule),
                )} and a ${absorber.code} both fall due, so the sheet shows one ${absorber.code}: the deeper clean absorbs the lighter one and the room is not cleaned twice.`}
              <Truncation plan={occupied} />
            </p>
          </div>
          <h3 className="pfres-section__subhead">After the guest leaves</h3>
          <div className="hk-guide-unit">
            <Strip plan={vacant} />
            <p className="hk-guide-legend">
              {cycleRules(active, "vacantInterval").map((rule) => (
                <React.Fragment key={rule.id}>
                  An empty room gets one {rule.code} every {rule.intervalNights}{" "}
                  nights, on that day only. It does not reappear on the days in
                  between. Completing it on time keeps that rhythm; recording
                  it on another day restarts the count from the actual day.{" "}
                </React.Fragment>
              ))}
              <Truncation plan={vacant} />
            </p>
          </div>
        </div>
      </section>
      <section className="pfres-section">
        <div className="pfres-section__header">
          <h2 className="pfres-section__title">What you will see on the day</h2>
          <p className="pfres-section__lede">
            The room&apos;s situation on a service day decides which clean is
            raised.
          </p>
        </div>
        <div className="pfres-section__body">
          <div className="pfres-table-wrap">
            <table className="pfres-table">
              <thead>
                <tr>
                  <th>The day</th>
                  <th>What appears</th>
                  <th>Watch for</th>
                </tr>
              </thead>
              <tbody>
                {dayRows(active).map((row) => (
                  <tr key={row.trigger}>
                    <th>
                      {row.title}
                      <span className="pfres-table__sub">{row.trigger}</span>
                    </th>
                    <td>{row.appears}</td>
                    <td>{row.watch}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="hk-guide-legend">
            A room marked do not disturb raises no cleaning at all for the whole
            stay. No-shows, holds, offsite and program bookings are not rooms to
            clean.
          </p>
        </div>
      </section>
      <section className="pfres-section">
        <div className="pfres-section__header">
          <h2 className="pfres-section__title">
            When one clean replaces another
          </h2>
          <p className="pfres-section__lede">
            A deeper clean absorbs the lighter work in the same stay, so a room
            is cleaned once. Absorbed rows stay on the sheet marked as combined,
            and they are counted once in the totals.
          </p>
        </div>
        <div className="pfres-section__body">
          <div className="pfres-table-wrap">
            <table className="pfres-table">
              <thead>
                <tr>
                  <th>Recording this</th>
                  <th>Also settles</th>
                  <th>Does not settle</th>
                </tr>
              </thead>
              <tbody>
                {absorptionRows(active).map(({ rule, settles }) => (
                  <tr key={rule.id}>
                    <td>
                      <Chip rule={rule} />
                    </td>
                    <td>
                      {settles.length > 0 ? codes(settles) : "Nothing else"}
                    </td>
                    <td>{notSettled(active, rule)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="hk-guide-note">
            A clean only settles work that was already due when it was actually
            carried out. A clean done on Tuesday does not settle work that falls
            due on Thursday.
          </p>
        </div>
      </section>
    </div>
  );
}
